/** Maps `rescueFosterHomes` onto the flat view-model the roster table and modals use. */

import type { HouseholdMemberGql, RescueFosterHomeGql, RescueFosterPlacementGql } from './rescueQueries';
import type { Device } from './types';
import { normalizePhoneDigits } from './utils';

/** A rescue's saved foster home, flattened for display. */
export interface FosterHome {
  /** `RescueFosterHome.id` — the argument for archive. */
  id: string;
  firstName: string;
  lastName: string | null;
  /** First + last, or just first. */
  fullName: string;
  email: string | null;
  phone: string | null;
  hasWifiSetup: boolean;
  hasSafeZone: boolean;
  createdAt: string;
  /** Placed right now. */
  placedPets: FosterPlacement[];
  /** The household the rescue places pets into. What a placement's pet has to actually be in. */
  fosterHouseholdId: string;
  /** Display names of the household's members, by role. */
  owners: string[];
  caretakers: string[];
  /**
   * Pets in the household right now. Ground truth for whether the home can be retired, since that's
   * what the backend checks — kept separate from `activePlacementCount`, which the ledger derives.
   */
  placedPetCount: number;
  /**
   * Open stays, counted server-side from the ledger. Drives the "Fostering (n)" / "Not
   * fostering" status, and is the column the roster's Status sort orders on — so it is read
   * off the response rather than recounted here, where a divergence would put a row in a
   * position its own number contradicts.
   */
  activePlacementCount: number;
  /** Lifetime placements, counted from the ledger server-side. Recalls don't remove a stay. */
  placementCount: number;
  /** Every recorded stay, newest first — including ones that have ended. */
  placements: FosterPlacementRecord[];
  /** The backend rejects archiving an occupied home, so the modal hides the action instead. */
  canArchive: boolean;
}

/** One stay in the foster household, open or closed. */
export interface FosterPlacementRecord {
  id: string;
  petName: string;
  fosteredAt: string;
  /** Null while the pet is still there. */
  recalledAt: string | null;
  endedReason: RescueFosterPlacementGql['endedReason'];
}

/** A pet in the foster household right now. */
export interface FosterPlacement {
  id: string;
  name: string;
}

export function adaptFosterHome(home: RescueFosterHomeGql): FosterHome {
  const firstName = home.contactFirstName.trim();
  const lastName = home.contactLastName?.trim() || null;
  const placedPets: FosterPlacement[] = home.fosterHousehold.pets.map((pet) => ({
    id: pet.id,
    name: pet.name,
  }));
  const memberName = (m: HouseholdMemberGql) => [m.firstName, m.lastName].filter(Boolean).join(' ').trim();

  return {
    id: home.id,
    fosterHouseholdId: home.fosterHousehold.id,
    firstName,
    lastName,
    fullName: lastName ? `${firstName} ${lastName}` : firstName,
    email: home.contactEmail?.trim() || null,
    phone: home.contactPhoneNumber?.trim() || null,
    hasWifiSetup: home.hasWifiSetup,
    hasSafeZone: home.hasSafeZone,
    createdAt: home.createdAt,
    placedPets,
    owners: home.fosterHousehold.owners.map(memberName).filter(Boolean),
    caretakers: home.fosterHousehold.caretakers.map(memberName).filter(Boolean),
    placedPetCount: placedPets.length,
    activePlacementCount: home.activePlacementCount,
    placementCount: home.placementCount,
    placements: home.placements,
    canArchive: placedPets.length === 0,
  };
}

export function adaptFosterHomes(homes: RescueFosterHomeGql[]): FosterHome[] {
  return homes.map(adaptFosterHome);
}

/**
 * The pet each open placement names, as ids, keyed by placement id.
 *
 * A placement records the pet's name at the time of the stay, not a foreign key, so the link back
 * is a name match against the pets currently in the foster household. Two rules fall out of that
 * and both are deliberate: a recalled stay resolves to nothing, and a name shared by two pets in
 * one home resolves to neither, because there is no way to tell which one the stay was about. The
 * match is exact — a rename, or a difference of case or whitespace, is a miss rather than a guess.
 *
 * Ids rather than pets: the home already carries `placedPets`, so deciding which rows link needs
 * no pet records at all. Whoever wants the pet itself fetches it by the id this returns.
 */
export function resolveOpenPlacementPetIds(home: FosterHome): Map<string, string> {
  const byPlacement = new Map<string, string>();

  for (const placement of home.placements) {
    if (placement.recalledAt) {
      continue;
    }
    const named = home.placedPets.filter((pet) => pet.name === placement.petName);
    if (named.length === 1) {
      byPlacement.set(placement.id, named[0].id);
    }
  }

  return byPlacement;
}

/** Whether the household's device setup carries forward whole, in part, or not at all. */
export type FosterSetupState = 'ready' | 'partial' | 'none';

export interface FosterSetup {
  state: FosterSetupState;
  /** What is already set up, or null when nothing is. */
  configured: string | null;
  /** What still needs setting up, or null when nothing does. */
  missing: string | null;
}

/**
 * How much of a foster household's setup a placement inherits.
 *
 * Both halves are named because the callers phrase opposite sides of the same fact — recall says
 * what the home keeps, the assign picker says what the volunteer still has to do — and `partial` is
 * worth its own state either way: keeping half the setup and adding the rest is a materially
 * different ask from starting over.
 *
 * Takes the two booleans rather than a `Device` or a `FosterHome` because both are callers, and they
 * spell the same two fields differently (`safeZone`/`wifiConfigured` against
 * `hasSafeZone`/`hasWifiSetup`).
 */
export function fosterSetup({ hasSafeZone, hasWifi }: { hasSafeZone: boolean; hasWifi: boolean }): FosterSetup {
  const SAFE_ZONE = 'Safe Zone';
  const WIFI = 'WiFi';
  const BOTH = 'Safe Zone and WiFi';

  if (hasSafeZone && hasWifi) {
    return { state: 'ready', configured: BOTH, missing: null };
  }
  if (!hasSafeZone && !hasWifi) {
    return { state: 'none', configured: null, missing: BOTH };
  }
  return {
    state: 'partial',
    configured: hasSafeZone ? SAFE_ZONE : WIFI,
    missing: hasSafeZone ? WIFI : SAFE_ZONE,
  };
}

/**
 * The saved home behind a device's foster, or null when the placement was made without one.
 *
 * Mirrors the backend's `findReusableFosterHomeForContact` tiering rather than taking whichever home
 * the array happens to offer first, because "first" is not an answer: two homes can share a landline,
 * and picking between them by position opens the wrong household from the roster's View.
 *
 *  - A home whose recorded phone *disagrees* with the device's is never a match, even when the email
 *    lines up. The backend's reason is access — reuse hands OWNER of the retained Places and wifi to
 *    whoever the phone invites — and this must not name a home the backend would refuse to reuse.
 *  - An email match beats a phone match, so a shared number can't outrank the contact's own address.
 *  - Within a tier the newest home wins, which is the backend's tie-break too.
 *
 * Callers must be able to live with a false negative — it only ever understates what the backend
 * will keep. `normalizePhoneDigits` is also a looser canonicalizer than the backend's, so treat a
 * match as "the backend would probably pick this one", never as authoritative.
 */
export function findFosterHomeForDevice(device: Device, homes: FosterHome[]): FosterHome | null {
  const email = device.holderEmail?.trim().toLowerCase() || null;
  const phone = normalizePhoneDigits(device.holderPhone) || null;
  if (!email && !phone) {
    return null;
  }

  const phoneAgrees = (home: FosterHome) => {
    const recorded = normalizePhoneDigits(home.phone) || null;
    return !recorded || !phone || recorded === phone;
  };

  const eligible = homes.filter(phoneAgrees);
  const emailMatches = email ? eligible.filter((home) => home.email?.trim().toLowerCase() === email) : [];
  const phoneMatches = phone ? eligible.filter((home) => normalizePhoneDigits(home.phone) === phone) : [];
  const candidates = emailMatches.length > 0 ? emailMatches : phoneMatches;

  // Newest wins, computed rather than assumed: the roster query returns newest-first today, but this
  // must not silently pick differently if a caller ever hands over a sorted copy.
  return candidates.reduce<FosterHome | null>(
    (newest, home) => (!newest || home.createdAt > newest.createdAt ? home : newest),
    null,
  );
}

/** Does this device's foster already have a saved home? See {@link findFosterHomeForDevice}. */
export function isFosterOnRoster(device: Device, homes: FosterHome[]): boolean {
  return findFosterHomeForDevice(device, homes) !== null;
}
